import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError } from "../api";
import { ImageUpload } from "./common";
import { PolygonEditor } from "./PolygonEditor";

function Poligon({ p, set, projectId }) {
  const { data: daireler = [] } = useQuery({ queryKey: ["adm-apts", projectId], queryFn: async () => (await api.get("/admin/apartments", { params: { project_id: projectId } })).data });
  const [blok, setBlok] = useState("");
  const [kat, setKat] = useState("");
  const bloklar = [...new Set(daireler.map((d) => d.blok))].sort();
  const katlar = [...new Set(daireler.filter((d) => d.blok === blok).map((d) => d.kat))].sort((a, b) => a - b);

  const blokDeger = (p.blok_polygons || []).map((x) => ({ label: x.blok, points: x.points }));
  const setBlokDeger = (list) => set("blok_polygons", list.map((x) => ({ blok: x.label, points: x.points })));

  const floorplans = p.floorplans || [];
  const fp = floorplans.find((f) => f.blok === blok && String(f.kat) === String(kat));
  const upsertFp = (patch) => {
    const others = floorplans.filter((f) => !(f.blok === blok && String(f.kat) === String(kat)));
    set("floorplans", [...others, { blok, kat: Number(kat), image: fp?.image || "", polygons: fp?.polygons || [], ...patch }]);
  };
  const fpDeger = (fp?.polygons || []).map((x) => ({ label: x.no, points: x.points }));
  const setFpDeger = (list) => upsertFp({ polygons: list.map((x) => ({ no: x.label, points: x.points })) });

  return (
    <div className="a-form">
      <h3 className="veri">1. Blok yerleşimi (proje render'ı)</h3>
      <p className="veri" style={{ color: "var(--metin-2)" }}>Render'ı yükleyin, üzerinde tıklayarak her blok için poligon çizin. Kapatırken blok adını (A, B) girin.</p>
      <PolygonEditor image={p.blok_render} onImage={(v) => set("blok_render", v)} value={blokDeger} onChange={setBlokDeger} labelSoru="Blok adı (ör. A)" testId="pg-blok" />

      <h3 className="veri" style={{ marginTop: 24 }}>2. Kat planı → daire poligonları</h3>
      <div className="a-satir">
        <select value={blok} onChange={(e) => { setBlok(e.target.value); setKat(""); }} data-testid="pg-blok-sec"><option value="">Blok seç</option>{bloklar.map((b) => <option key={b}>{b}</option>)}</select>
        <select value={kat} onChange={(e) => setKat(e.target.value)} disabled={!blok} data-testid="pg-kat-sec"><option value="">Kat seç</option>{katlar.map((k) => <option key={k} value={k}>{k}. kat</option>)}</select>
      </div>
      {blok && kat ? (
        <PolygonEditor image={fp?.image} onImage={(v) => upsertFp({ image: v })} value={fpDeger} onChange={setFpDeger} labelSoru="Daire no (ör. A31)" testId="pg-daire" />
      ) : <p className="a-bos">Kat planı için blok ve kat seçin.</p>}
    </div>
  );
}


function Genel({ p, set }) {
  const setK = (k, v) => set("kunye", { ...(p.kunye || {}), [k]: v });
  return (
    <div className="a-form">
      <div className="grid2">
        <div><label>Proje adı</label><input value={p.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="pe-name" /></div>
        <div><label>Slug (boş bırakırsan addan üretilir)</label><input value={p.slug || ""} onChange={(e) => set("slug", e.target.value)} /></div>
      </div>
      <div className="grid2">
        <div><label>Konum</label><input value={p.location || ""} onChange={(e) => set("location", e.target.value)} /></div>
        <div><label>Durum</label><select value={p.status || "yakinda"} onChange={(e) => set("status", e.target.value)} data-testid="pe-status"><option value="devam-eden">Devam eden</option><option value="tamamlandi">Tamamlandı</option><option value="yakinda">Yakında</option></select></div>
      </div>
      <div><label>Özet (kart satırı)</label><input value={p.summary || ""} onChange={(e) => set("summary", e.target.value)} /></div>
      <div><label>Tanıtım metni</label><textarea rows={4} value={p.intro || ""} onChange={(e) => set("intro", e.target.value)} /></div>
      <div className="grid2">
        <div><label>Gündüz görseli</label><ImageUpload value={p.hero_gunduz} onChange={(v) => set("hero_gunduz", v)} testId="pe-hero-gunduz" /></div>
        <div><label>Gece görseli (boşsa gündüzden otomatik üretilir)</label><ImageUpload value={p.hero_gece} onChange={(v) => set("hero_gece", v)} testId="pe-hero-gece" /></div>
      </div>
      <div className="grid2">
        <label className="a-satir"><input type="checkbox" checked={!!p.has_apartments} onChange={(e) => set("has_apartments", e.target.checked)} /> Daire seçimi var</label>
        <label className="a-satir"><input type="checkbox" checked={!!p.has_floor_view} onChange={(e) => set("has_floor_view", e.target.checked)} /> Kat manzara var</label>
      </div>
      <div><label>Harita gömme (iframe src)</label><input value={p.map_embed || ""} onChange={(e) => set("map_embed", e.target.value)} /></div>
      <div><label>Sıra</label><input type="number" value={p.order ?? 99} onChange={(e) => set("order", Number(e.target.value))} /></div>

      <h3 className="veri" style={{ marginTop: 10 }}>Künye</h3>
      <div className="grid2">
        {["blok", "kat", "daire_sayisi", "teslim", "m2"].map((k) => (
          <div key={k}><label>{k}</label><input value={p.kunye?.[k] || ""} onChange={(e) => setK(k, e.target.value)} /></div>
        ))}
      </div>

      <ListeDuzenle baslik="Mesafeler" alan="distances" p={p} set={set} alanlar={["label", "value"]} />
      <ListeDuzenle baslik="Olanaklar" alan="olanaklar" p={p} set={set} alanlar={["no", "text"]} />
      <ListeDuzenle baslik="Ödeme planları" alan="payment_plans" p={p} set={set} alanlar={["baslik", "detay"]} />
      <SpecEditor p={p} set={set} />
    </div>
  );
}

function ListeDuzenle({ baslik, alan, p, set, alanlar }) {
  const liste = p[alan] || [];
  const upd = (i, k, v) => { const l = [...liste]; l[i] = { ...l[i], [k]: v }; set(alan, l); };
  return (
    <div>
      <h3 className="veri" style={{ marginTop: 10, marginBottom: 6 }}>{baslik}</h3>
      {liste.map((row, i) => (
        <div className="a-satir" key={i} style={{ marginBottom: 6 }}>
          {alanlar.map((k) => <input key={k} placeholder={k} value={row[k] || ""} onChange={(e) => upd(i, k, e.target.value)} />)}
          <button type="button" className="a-btn hayalet kucuk" onClick={() => set(alan, liste.filter((_, j) => j !== i))}>Sil</button>
        </div>
      ))}
      <button type="button" className="a-btn hayalet kucuk" onClick={() => set(alan, [...liste, {}])}>+ Ekle</button>
    </div>
  );
}

function SpecEditor({ p, set }) {
  const liste = p.specs || [];
  const upd = (i, k, v) => { const l = [...liste]; l[i] = { ...l[i], [k]: v }; set("specs", l); };
  return (
    <div>
      <h3 className="veri" style={{ marginTop: 10, marginBottom: 6 }}>Teknik şartname</h3>
      {liste.map((row, i) => (
        <div key={i} style={{ marginBottom: 8, border: "1px solid var(--cizgi)", padding: 10 }}>
          <input placeholder="Başlık" value={row.baslik || ""} onChange={(e) => upd(i, "baslik", e.target.value)} style={{ marginBottom: 6 }} />
          <textarea rows={3} placeholder="Her satıra bir madde" value={(row.maddeler || []).join("\n")} onChange={(e) => upd(i, "maddeler", e.target.value.split("\n").filter(Boolean))} />
          <button type="button" className="a-btn hayalet kucuk" style={{ marginTop: 6 }} onClick={() => set("specs", liste.filter((_, j) => j !== i))}>Sil</button>
        </div>
      ))}
      <button type="button" className="a-btn hayalet kucuk" onClick={() => set("specs", [...liste, { baslik: "", maddeler: [] }])}>+ Bölüm ekle</button>
    </div>
  );
}

function Galeri({ p, set }) {
  const g = p.gallery || [];
  return (
    <div className="a-form">
      <div className="a-galeri">
        {g.map((u, i) => (
          <div className="g" key={i}>
            <img src={u.startsWith("data:") || u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`} alt="" />
            <button type="button" className="sil" onClick={() => set("gallery", g.filter((_, j) => j !== i))}>✕</button>
          </div>
        ))}
      </div>
      <div><label>Görsel ekle</label><ImageUpload value="" onChange={(u) => u && set("gallery", [...g, u])} testId="pe-galeri-ekle" /></div>
    </div>
  );
}

function Teslim({ p, set }) {
  const d = p.delivery || {};
  const setD = (k, v) => set("delivery", { ...d, [k]: v });
  return (
    <div className="a-form">
      <p className="veri">Tarihleri YYYY-MM olarak girin (ör. 2024-03). Etiket kullanıcıya gösterilir.</p>
      <div className="grid2">
        <div><label>Söz verilen (YYYY-MM)</label><input value={d.promised_date || ""} onChange={(e) => setD("promised_date", e.target.value)} data-testid="pe-promised" /></div>
        <div><label>Söz verilen etiket</label><input value={d.promised_label || ""} onChange={(e) => setD("promised_label", e.target.value)} placeholder="Mart 2024" /></div>
      </div>
      <div className="grid2">
        <div><label>Gerçekleşen (YYYY-MM)</label><input value={d.actual_date || ""} onChange={(e) => setD("actual_date", e.target.value)} data-testid="pe-actual" /></div>
        <div><label>Gerçekleşen etiket</label><input value={d.actual_label || ""} onChange={(e) => setD("actual_label", e.target.value)} placeholder="Şubat 2024" /></div>
      </div>
      <div><label>Gecikme açıklaması (varsa)</label><textarea rows={2} value={d.delay_note || ""} onChange={(e) => setD("delay_note", e.target.value)} /></div>
      <button type="button" className="a-btn hayalet kucuk" onClick={() => set("delivery", null)}>Teslim kaydını temizle</button>
    </div>
  );
}

function Daireler({ projectId }) {
  const qc = useQueryClient();
  const { data: daireler = [] } = useQuery({ queryKey: ["adm-apts", projectId], queryFn: async () => (await api.get("/admin/apartments", { params: { project_id: projectId } })).data });
  const [sel, setSel] = useState([]);
  const upd = async (id, k, v) => { await api.put(`/admin/apartments/${id}`, { [k]: v }); qc.invalidateQueries({ queryKey: ["adm-apts", projectId] }); };
  const bulkStatus = async (durum) => { if (!sel.length) return; await api.post("/admin/apartments/bulk-status", { ids: sel, durum }); qc.invalidateQueries({ queryKey: ["adm-apts", projectId] }); toast.success("Güncellendi"); };
  const bulkPrice = async () => {
    const v = prompt("Yüzde değişim (%). Örn 10 => %10 zam, -5 => %5 indirim:");
    if (v === null) return;
    await api.post("/admin/apartments/bulk-price", { ids: sel, mode: "percent", value: Number(v) });
    qc.invalidateQueries({ queryKey: ["adm-apts", projectId] }); toast.success("Fiyatlar güncellendi");
  };
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  if (daireler.length === 0) return <div className="a-bos">Bu projede daire yok. (Kadıköy projesinde 40 daire seedlenmiştir.)</div>;
  return (
    <div>
      <div className="a-arac">
        <span className="veri">{sel.length} seçili</span>
        <button className="a-btn hayalet kucuk" onClick={() => bulkStatus("musait")}>Müsait yap</button>
        <button className="a-btn hayalet kucuk" onClick={() => bulkStatus("satildi")}>Satıldı yap</button>
        <button className="a-btn hayalet kucuk" onClick={bulkPrice}>Toplu fiyat (%)</button>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table className="a-tablo">
          <thead><tr><th></th><th>No</th><th>Blok</th><th>Kat</th><th>Oda</th><th>m²</th><th>Cephe</th><th>Fiyat</th><th>Durum</th></tr></thead>
          <tbody>
            {daireler.map((d) => (
              <tr key={d.id}>
                <td><input type="checkbox" checked={sel.includes(d.id)} onChange={() => toggle(d.id)} /></td>
                <td className="sayi">{d.no}</td><td>{d.blok}</td><td className="sayi">{d.kat}</td><td>{d.oda}</td><td className="sayi">{d.m2}</td><td>{d.cephe}</td>
                <td><input className="sayi" defaultValue={d.fiyat} onBlur={(e) => upd(d.id, "fiyat", Number(e.target.value))} style={{ width: 110 }} /></td>
                <td><select value={d.durum} onChange={(e) => upd(d.id, "durum", e.target.value)} data-testid={`adm-daire-durum-${d.no}`}><option value="musait">Müsait</option><option value="opsiyonlu">Opsiyonlu</option><option value="satildi">Satıldı</option></select></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Tunel({ projectId }) {
  const qc = useQueryClient();
  const { data: kareler = [] } = useQuery({ queryKey: ["adm-tl", projectId], queryFn: async () => (await api.get("/admin/timeline", { params: { project_id: projectId } })).data });
  const [yeni, setYeni] = useState({ ay: "", label: "", yuzde: 0, not_: "", foto: "" });
  const ekle = async () => { await api.post("/admin/timeline", { ...yeni, project_id: projectId }); setYeni({ ay: "", label: "", yuzde: 0, not_: "", foto: "" }); qc.invalidateQueries({ queryKey: ["adm-tl", projectId] }); toast.success("Kare eklendi"); };
  const sil = async (id) => { await api.delete(`/admin/timeline/${id}`); qc.invalidateQueries({ queryKey: ["adm-tl", projectId] }); };
  return (
    <div className="a-form">
      {kareler.map((k) => (
        <div className="a-satir" key={k.id} style={{ borderBottom: "1px solid var(--cizgi)", paddingBottom: 8 }}>
          <img src={k.foto?.startsWith("data:") || k.foto?.startsWith("http") ? k.foto : `${process.env.REACT_APP_BACKEND_URL}${k.foto}`} className="a-thumb" alt="" />
          <div><b>{k.label}</b> · %{k.yuzde}<br /><span className="veri">{k.not_}</span></div>
          <button className="a-btn hayalet kucuk" onClick={() => sil(k.id)}>Sil</button>
        </div>
      ))}
      <div style={{ border: "1px solid var(--cizgi)", padding: 12 }}>
        <div className="grid2">
          <div><label>Ay (YYYY-MM)</label><input value={yeni.ay} onChange={(e) => setYeni({ ...yeni, ay: e.target.value })} data-testid="tl-ay" /></div>
          <div><label>Etiket</label><input value={yeni.label} onChange={(e) => setYeni({ ...yeni, label: e.target.value })} placeholder="Mart 2025" /></div>
        </div>
        <div><label>Tamamlanma %</label><input type="number" value={yeni.yuzde} onChange={(e) => setYeni({ ...yeni, yuzde: Number(e.target.value) })} data-testid="tl-yuzde" /></div>
        <div><label>Not</label><input value={yeni.not_} onChange={(e) => setYeni({ ...yeni, not_: e.target.value })} /></div>
        <div><label>Fotoğraf</label><ImageUpload value={yeni.foto} onChange={(v) => setYeni({ ...yeni, foto: v })} testId="tl-foto" /></div>
        <button className="a-btn bal kucuk" style={{ marginTop: 10 }} onClick={ekle} data-testid="tl-ekle">+ Kare ekle</button>
      </div>
    </div>
  );
}

function Manzara({ projectId }) {
  const qc = useQueryClient();
  const { data: katlar = [] } = useQuery({ queryKey: ["adm-fv", projectId], queryFn: async () => (await api.get("/admin/floor-views", { params: { project_id: projectId } })).data });
  const upd = async (id, foto) => { await api.put(`/admin/floor-views/${id}`, { foto }); qc.invalidateQueries({ queryKey: ["adm-fv", projectId] }); };
  if (katlar.length === 0) return <div className="a-bos">Kat manzara kaydı yok.</div>;
  return (
    <div className="a-form">
      {katlar.map((k) => (
        <div className="a-satir" key={k.id} style={{ borderBottom: "1px solid var(--cizgi)", paddingBottom: 8 }}>
          <div style={{ width: 90 }}><b>{k.kat}. kat</b><br /><span className="veri">{k.kot}</span></div>
          <ImageUpload value={k.foto} onChange={(v) => upd(k.id, v)} testId={`fv-${k.kat}`} />
        </div>
      ))}
    </div>
  );
}

export default function ProjectEdit() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { data: liste = [] } = useQuery({ queryKey: ["admin-projects"], queryFn: async () => (await api.get("/admin/projects")).data });
  const orijinal = liste.find((x) => x.id === id);
  const [p, setP] = useState(null);
  const [tab, setTab] = useState("genel");
  const [dirty, setDirty] = useState(false);

  if (!orijinal) return <p className="veri">Yükleniyor…</p>;
  const cur = p || orijinal;
  const set = (k, v) => { setP({ ...cur, [k]: v }); setDirty(true); };

  const kaydet = async () => {
    try {
      await api.put(`/admin/projects/${id}`, cur);
      qc.invalidateQueries({ queryKey: ["admin-projects"] });
      setDirty(false); toast.success("Kaydedildi");
    } catch (e) { toast.error(apiError(e)); }
  };

  const tabs = [["genel", "Genel"], ["galeri", "Galeri"], ["teslim", "Teslim Karnesi"], ["daireler", "Daireler"], ["poligon", "Kat Planı"], ["tunel", "Zaman Tüneli"], ["manzara", "Kat Manzara"]];

  return (
    <div>
      <div className="a-satir" style={{ justifyContent: "space-between", marginBottom: 16 }}>
        <div><Link to="/admin/projeler" className="veri">← Projeler</Link><h1 style={{ fontFamily: "var(--display)", fontSize: 24 }}>{cur.name}</h1></div>
        <button className="a-btn bal" onClick={kaydet} disabled={!dirty} data-testid="pe-kaydet">{dirty ? "Kaydet" : "Kaydedildi"}</button>
      </div>
      <div className="adimlar" style={{ marginBottom: 20 }}>
        {tabs.map(([k, l]) => <button key={k} className={`adim ${tab === k ? "aktif" : ""}`} onClick={() => setTab(k)} data-testid={`pe-tab-${k}`}>{l}</button>)}
      </div>
      {tab === "genel" && <Genel p={cur} set={set} />}
      {tab === "galeri" && <Galeri p={cur} set={set} />}
      {tab === "teslim" && <Teslim p={cur} set={set} />}
      {tab === "daireler" && <Daireler projectId={id} />}
      {tab === "poligon" && <Poligon p={cur} set={set} projectId={id} />}
      {tab === "tunel" && <Tunel projectId={id} />}
      {tab === "manzara" && <Manzara projectId={id} />}
    </div>
  );
}
